import { createContext } from '@shared/utils/createContext';
import React, { useCallback, useState } from 'react';
import { useAdminRegistryTelemetry } from '@apps/registry/admin/AdminRegistry.telemetry';
import { getCurrencySymbolFromCurrencyCode } from '@shared/utils/currency/utils';
import type { RegistryCurrencyCodesFragment } from '@graphql/generated';
import { AddCustomGiftDialog } from '@apps/registry/common/components/AddCustomGiftDialog';
import { CurrencyDialog } from '@apps/registry/common/components/CurrencyDialog';

type AddGiftContext = {
  // State
  isOpen: boolean;
  // Operations
  openModal: () => void;
  closeModal: () => void;
};

const [Provider, useAddCustomGiftDialogContext] = createContext<AddGiftContext>({ name: 'AddGiftContextProvider' });

interface PurchaseConfirmationProviderProps
  extends Readonly<{
    eventId: Maybe<string>;
    children: React.ReactNode;
    registryCurrency: Maybe<RegistryCurrencyCodesFragment>;
  }> {}

const AddCustomGiftDialogProvider = ({ children, eventId, registryCurrency }: PurchaseConfirmationProviderProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isOpenCurrencyDialog, setIsOpenCurrencyDialog] = useState<boolean>(false);
  const telemetry = useAdminRegistryTelemetry();

  const onOpen = useCallback(() => {
    telemetry.clickAddGiftManually('Product', 'Add Gift dialog', 'StartAddCustomProductDialog', eventId || '');
    setIsOpen(true);
  }, [eventId, telemetry]);

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  return (
    <Provider
      value={{
        isOpen,
        closeModal: onClose,
        openModal: onOpen,
      }}
    >
      <AddCustomGiftDialog
        eventId={eventId || ''}
        isOpen={isOpen}
        onClose={onClose}
        currency={getCurrencySymbolFromCurrencyCode(registryCurrency?.code)}
        showCurrencyDialog={() => setIsOpenCurrencyDialog(true)}
      />
      <CurrencyDialog
        isOpen={isOpenCurrencyDialog}
        onClose={() => setIsOpenCurrencyDialog(false)}
        eventId={eventId}
        registryCurrency={registryCurrency}
      />
      {children}
    </Provider>
  );
};

AddCustomGiftDialogProvider.displayName = 'AddCustomGiftDialogProvider';

export { AddCustomGiftDialogProvider, useAddCustomGiftDialogContext };
